import { useLocation, useMatch } from 'react-router-dom';
import {
  isPublicProjectPathname,
  NODON_GENERATION_PROJECT_MATCH_PATH,
  NODON_SHARED_PROJECT_MATCH_PATH,
} from '@shared/constants/router.constants';
import {
  getProject,
  getProjectsLookup,
  useProject,
} from '@client/store/project';
import {
  IProjectInfo,
  OneOfListElements,
  Project,
} from '@shared/models/project.model';
import { canWrite, isAdminGroupMember } from '@shared/helpers/user.helpers';
import { UserState, useUserStore } from '@client/store/user/user.store';
import { useSelectedOrganizationName } from '@client/store/organization';
import { User } from '@shared/models/user.model';
import { SHARED_PROJECT } from '@shared/constants';
import { useObjectMemo } from './use-object.hooks';
import { isGenerated } from '@shared/helpers/element.helpers';
import { isOneOfChildElements } from '@shared/helpers/recursive-element.helpers';

const userSelector = (state: UserState) => state.user;
export const useUser = (): User | undefined => useUserStore(userSelector);

const usersSelector = (state: UserState) => state.users;
export const useUsers = (): User[] => useUserStore(usersSelector);

const lookupSelector = (state: UserState) => state.lookup;
export const useUserLookup = (): UserState['lookup'] =>
  useUserStore(lookupSelector);

/**
 * Get user without causing re-renders
 * @returns
 */
export const getUser = (): User | undefined =>
  userSelector(useUserStore.getState());

/**
 * Check if the user can write to the project
 * @param changes - If updating lock/archived, the new values will be required to verify changes
 * @returns
 */
export const useIsSharedLink = (): boolean => !!useMatch('/shared/*');

export const useIsReadonly = (changes: Partial<Project> = {}): boolean => {
  const user = useUser();
  const project = useProject();
  const organization = useSelectedOrganizationName();
  const { pathname } = useLocation();
  const memoChanges = useObjectMemo(changes);

  if (isPublicProjectPathname(pathname)) return true;
  if (!user || !organization) return false;

  return !canWrite(user.id, organization, project, memoChanges);
};

/**
 * Check if the user can write to the element
 * @param element
 * @returns
 */
export const useIsReadonlyElement = (element?: OneOfListElements): boolean => {
  if (useIsReadonly()) {
    return true;
  }
  return isOneOfChildElements(element) && isGenerated(element);
};

export const useUserId = (): string => {
  const user = useUser();
  const { pathname } = useLocation();
  const sharedProjectMatch = useMatch(NODON_SHARED_PROJECT_MATCH_PATH);
  const generationProjectMatch = useMatch(NODON_GENERATION_PROJECT_MATCH_PATH);

  if (
    !user &&
    !isPublicProjectPathname(pathname) &&
    !sharedProjectMatch &&
    !generationProjectMatch
  ) {
    throw new Error('User not logged in');
  }
  return isPublicProjectPathname(pathname) ||
    sharedProjectMatch ||
    generationProjectMatch
    ? SHARED_PROJECT
    : (user?.id ?? '');
};

export const useUserEmail = (): string => {
  const user = useUser();
  return user?.name ?? '';
};

export const useUserDomain = (): string => {
  const email = useUserEmail();
  const domain = email.split('@')[1];

  return domain ?? '';
};

export const useUserIsOwner = (id = getProject().id): boolean => {
  const user = useUser();
  const project = getProjectsLookup()[id] as IProjectInfo | undefined;

  return user?.id === project?.owner;
};

export const useUserIsAdmin = (): boolean => {
  const user = useUser();
  return user ? isAdminGroupMember(user) : false;
};
